/**
 * Phase 20 — the campaign groups' auto-send rows, pure: one per master
 * config of the plan (the plan detail always lists them), with today's facts
 * from the scheduler status when it has them — the status leaves out a plan
 * that is disabled, killed or without a time zone. The layer a row edits,
 * and each container's auto-send as Live shows it.
 * For Media Devoted | Created By Ciro Autiero
 */
import { type DropGenGroupSendStatus, type DropGenSchedulerPlanStatus, haltedOn } from '@trafficpulse/shared';

import type { DropGenContainerSummary, DropGenPlanDetail } from '../../../../drop-generator.types';
import type { LiveAutoSend } from '../../../live/ContainerLiveCard';
import type { AutoSendLayer } from '../settings/autosend-draft';

export function groupRows(
  plan: DropGenPlanDetail,
  status: DropGenSchedulerPlanStatus | undefined,
): DropGenGroupSendStatus[] {
  return plan.masters.map((m) => {
    const live = status?.groups.find((g) => g.masterId === m.id);
    if (live) return live;
    return {
      masterId: m.id,
      name: m.name,
      color: m.color,
      containerCount: m.followerCount,
      config: m.autoSend,
      haltedToday: status ? haltedOn(m.autoSend, status.runDate) : false,
      sendToday: null,
    };
  });
}

/** The settings dialog's layer for a group row. */
export const groupLayer = (g: DropGenGroupSendStatus): AutoSendLayer => ({
  id: g.masterId,
  kind: 'group',
  name: g.name,
  groupName: g.name,
  config: g.config,
});

/** Each container's auto-send for its Live card: its group's switch, then
 *  its own layer (whose switch can only take it out), then every halt. */
export function liveAutoSend(
  plan: DropGenPlanDetail,
  status: DropGenSchedulerPlanStatus | undefined,
): (containerId: string) => LiveAutoSend {
  const groups = new Map(groupRows(plan, status).map((g) => [g.masterId, g]));
  const countryStop = plan.killSwitch || !plan.enabled || plan.autoSendHaltKind === 'TECHNICAL';
  const byId = new Map(plan.containers.map((c) => [c.id, c]));
  return (containerId) => {
    const c = byId.get(containerId);
    const group = c?.masterId ? groups.get(c.masterId) : undefined;
    if (!c || !group?.config.autoSend) return null;
    const own = c.ownAutoSend ?? null;
    if (own && !own.autoSend) return null; // kept for a person
    if (countryStop || group.haltedToday || ownHalted(c, status)) return 'halted';
    return group.config.shadow || own?.shadow ? 'shadow' : 'sending';
  };
}

const ownHalted = (c: DropGenContainerSummary, status: DropGenSchedulerPlanStatus | undefined) =>
  Boolean(c.ownAutoSend && status && haltedOn(c.ownAutoSend, status.runDate));
